/**
 * Shared voice-command text matching (editor mic + triggers).
 * Case-insensitive leading phrases; fuzzy best-match for static commands
 * (same idea as findBestTemplate in voiceTemplateEngine.js).
 */

/** Minimum similarity for fuzzy static commands (undo, delete word, bold, …). */
export const MIN_FUZZY_COMMAND_SCORE = 0.87;
export const MIN_FUZZY_COMMAND_GAP = 0.05;

const HYPHEN_CLASS = "\\s\\-‐‑‒–—―";
const BIDI_RE = /[\u200E\u200F\u202A-\u202E\u2066-\u2069]/g;
const HEBREW_LETTER = "\\u0590-\\u05FF";

const stripBidi = (text) => String(text || "").replace(BIDI_RE, "").trim();

/**
 * Normalize transcript text for comparison (case-insensitive, spaces/hyphens stripped).
 * @param {string} text
 * @returns {string}
 */
export const normalizeTranscript = (text) => {
  if (!text) return "";
  return text
    .trim()
    .toLowerCase()
    .replace(/[\s\-‐‑‒–—―]/g, "");
};

/**
 * Normalize for static command matching — strips optional STT filler words
 * (e.g. English "the", Hebrew "את" / definite ה on the next word).
 */
export const normalizeCommandTranscript = (text, langCode = "en") => {
  let n = normalizeTranscript(text);
  if (!n) return n;

  switch (langCode) {
    case "en":
      n = n.replace(/^select(?:the|a|an)/, "select");
      break;
    case "es":
      n = n.replace(/^seleccionar(?:el|la|los|las)/, "seleccionar");
      break;
    case "fr":
      n = n.replace(
        /^sélectionn(?:er|ez|e)(?:le|la|les|l)?/,
        "sélectionner",
      );
      break;
    case "he":
      if (n.startsWith("בחר")) {
        let rest = n.slice(3);
        if (rest.startsWith("את")) rest = rest.slice(2);
        // STT often adds definite article ה on nouns (הפסקה, הנוכחית, המשפט הבא)
        // Do not strip ה from הבא/הבאה — that ה is part of "next", not a definite article.
        rest = rest.replace(/ה(משפט|פסקה|שדה|נוכחית|נוכחי|קודם)/g, "$1");
        n = `בחר${rest}`;
      }
      break;
    case "ar":
      if (n.startsWith("اختر")) {
        let rest = n.slice(4);
        if (rest.startsWith("ال")) rest = rest.slice(2);
        n = `اختر${rest}`;
      }
      break;
    default:
      break;
  }

  return n;
};

const escapeRegExp = (value) =>
  String(value).replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

const levenshteinDistance = (a = "", b = "") => {
  const s = String(a);
  const t = String(b);
  const m = s.length;
  const n = t.length;
  if (m === 0) return n;
  if (n === 0) return m;

  const dp = Array.from({ length: m + 1 }, () => new Array(n + 1).fill(0));
  for (let i = 0; i <= m; i += 1) dp[i][0] = i;
  for (let j = 0; j <= n; j += 1) dp[0][j] = j;

  for (let i = 1; i <= m; i += 1) {
    for (let j = 1; j <= n; j += 1) {
      const cost = s[i - 1] === t[j - 1] ? 0 : 1;
      dp[i][j] = Math.min(
        dp[i - 1][j] + 1,
        dp[i][j - 1] + 1,
        dp[i - 1][j - 1] + cost,
      );
    }
  }
  return dp[m][n];
};

/** Similarity on compact strings (0–1); shared with heading / command matchers. */
export const compactStringSimilarity = (a = "", b = "") => {
  const maxLen = Math.max(a.length, b.length);
  if (maxLen === 0) return 1;
  return 1 - levenshteinDistance(a, b) / maxLen;
};

const commandStringSimilarity = compactStringSimilarity;

/**
 * Slice transcript after a leading phrase (any casing / spacing variants).
 * @returns {string|null}
 */
export const sliceAfterLeadingPhrase = (transcript, phrase) => {
  const trimmed = String(transcript || "").trim();
  const p = String(phrase || "").trim();
  if (!trimmed || !p) return null;

  const parts = p.split(/\s+/).filter(Boolean).map(escapeRegExp);
  const pattern = parts.join(`[${HYPHEN_CLASS}]+`);
  const re = new RegExp(`^${pattern}(?=$|[${HYPHEN_CLASS}])`, "iu");
  const m = trimmed.match(re);
  if (!m) return null;
  return trimmed.slice(m[0].length).trim();
};

/**
 * First matching leading trigger/command phrase (normalized prefix + case-insensitive slice).
 * @param {string} transcript
 * @param {string[]} phrases
 * @returns {{ phrase: string, remainder: string } | null}
 */
/**
 * Hebrew "go to" — STT often merges the preposition ל onto the heading
 * (e.g. "לך לממצאים" not "לך ל ממצאים"). See voice-test `he-go-to`.
 * @returns {{ phrase: string, remainder: string } | null}
 */
function matchHebrewGoToNavigation(transcript) {
  const trimmed = stripBidi(transcript);
  if (!trimmed) return null;

  const patterns = [
    /^לך\s+ל\s+(.+)$/u,
    // לך + ל attached to Hebrew heading word (לממצאים → header ממצאים)
    new RegExp(`^לך\\s+ל(?=[${HEBREW_LETTER}])(.+)$`, "u"),
    /^לך\s+(.+)$/u,
  ];

  for (const re of patterns) {
    const m = trimmed.match(re);
    if (m?.[1]?.trim()) {
      return { phrase: "לך ל", remainder: m[1].trim() };
    }
  }

  const norm = normalizeTranscript(trimmed);
  if (norm.startsWith("לכל")) {
    const attached = trimmed.match(/^לך\s*ל(.+)$/u);
    if (attached?.[1]?.trim()) {
      return { phrase: "לך ל", remainder: attached[1].trim() };
    }
    const fallback = trimmed.match(/^לך\s*(.+)$/u);
    if (fallback?.[1]?.trim()) {
      return { phrase: "לך ל", remainder: fallback[1].trim() };
    }
  }

  return null;
}

/**
 * "Go to" trigger at start — language-aware (Hebrew ל+heading merge, Latin phrase list).
 */
export function matchNavigationLeading(transcript, navPhrases, langCode = "en") {
  const trimmed = stripBidi(transcript);
  if (!trimmed) return null;

  if (langCode === "he") {
    const he = matchHebrewGoToNavigation(trimmed);
    if (he) return he;
  }

  return matchLeadingNormalizedPhrase(trimmed, navPhrases);
}

export const matchLeadingNormalizedPhrase = (transcript, phrases) => {
  const trimmed = stripBidi(transcript);
  if (!trimmed) return null;

  const normTranscript = normalizeTranscript(trimmed);
  const list = Array.isArray(phrases) ? phrases : [];

  for (const phrase of list) {
    const p = String(phrase || "").trim();
    if (!p) continue;
    const normPhrase = normalizeTranscript(p);
    if (!normPhrase || !normTranscript.startsWith(normPhrase)) continue;

    const remainder = sliceAfterLeadingPhrase(trimmed, p);
    if (remainder === null) continue;

    return { phrase: p, remainder };
  }
  return null;
};

/**
 * Strip a leading trigger word (template / macro). Case-insensitive for Latin triggers.
 * @param {string} trimmed
 * @param {string[]} triggers
 */
export const consumeLeadingTriggerPhrases = (trimmed, triggers) => {
  const match = matchLeadingNormalizedPhrase(trimmed, triggers);
  if (!match) return { matched: false, tail: trimmed };
  return { matched: true, tail: match.remainder };
};

/**
 * @param {string} normalizedTranscript
 * @param {Array<{ phrase: string, normalized: string, commandInfo: object }>} candidates
 * @returns {object|null} commandInfo
 */
export const findExactStaticCommand = (normalizedTranscript, candidates) => {
  if (!normalizedTranscript) return null;
  const hit = candidates.find((c) => c.normalized === normalizedTranscript);
  return hit?.commandInfo ?? null;
};

/**
 * Best fuzzy static command (STT typos / minor garbling). Requires score gap vs runner-up.
 * @returns {object|null} commandInfo
 */
export const findBestFuzzyStaticCommand = (normalizedTranscript, candidates) => {
  if (!normalizedTranscript) return null;

  let best = null;
  let second = null;

  for (const c of candidates) {
    if (!c.normalized) continue;
    const score = commandStringSimilarity(normalizedTranscript, c.normalized);
    if (score < MIN_FUZZY_COMMAND_SCORE) continue;

    if (!best || score > best.score) {
      second = best;
      best = { commandInfo: c.commandInfo, score, candidate: c };
    } else if (!second || score > second.score) {
      second = { score };
    }
  }

  if (!best) return null;
  if (second && best.score - second.score < MIN_FUZZY_COMMAND_GAP) return null;
  return best;
};

/**
 * Fuzzy match when the spoken phrase is the command plus optional tail text.
 */
export const findBestFuzzyLeadingStaticCommand = (
  normalizedTranscript,
  candidates,
) => {
  if (!normalizedTranscript) return null;

  let best = null;
  let second = null;

  for (const c of candidates) {
    if (!c.normalized) continue;
    const len = c.normalized.length;
    if (len < 4 || normalizedTranscript.length < len - 2) continue;

    const spokenPrefix =
      normalizedTranscript.length >= len
        ? normalizedTranscript.slice(0, len)
        : normalizedTranscript;
    const score = commandStringSimilarity(spokenPrefix, c.normalized);
    if (score < MIN_FUZZY_COMMAND_SCORE) continue;

    if (!best || score > best.score) {
      second = best;
      best = { commandInfo: c.commandInfo, score, candidate: c };
    } else if (!second || score > second.score) {
      second = { score };
    }
  }

  if (!best) return null;
  if (second && best.score - second.score < MIN_FUZZY_COMMAND_GAP) return null;
  return best;
};

const remainderAfterPhraseWords = (transcript, phrase) => {
  const trimmed = String(transcript || "").trim();
  const wordCount = String(phrase || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean).length;
  if (!trimmed || wordCount < 1) return "";
  const parts = trimmed.split(/\s+/).filter(Boolean);
  if (parts.length <= wordCount) return "";
  return parts.slice(wordCount).join(" ");
};

/**
 * Index of the earliest later command phrase inside text (for "go to X undo" splits).
 * @param {string} transcript
 * @param {string[]} phrases
 * @param {{ minIndex?: number }} [opts]
 * @returns {number} index or -1
 */
export const findEarliestCommandStartIndex = (
  transcript,
  phrases,
  { minIndex = 1 } = {},
) => {
  const trimmed = String(transcript || "").trim();
  if (!trimmed) return -1;

  let earliest = -1;
  const list = Array.isArray(phrases) ? phrases : [];

  for (const phrase of list) {
    const p = String(phrase || "").trim();
    if (!p) continue;
    const parts = p.split(/\s+/).filter(Boolean).map(escapeRegExp);
    const pattern = parts.join(`[${HYPHEN_CLASS}]+`);
    const re = new RegExp(
      `(?:^|[${HYPHEN_CLASS}])(${pattern})(?=$|[${HYPHEN_CLASS}])`,
      "giu",
    );
    let m;
    while ((m = re.exec(trimmed)) !== null) {
      const matchStart = m.index + (m[0].length - m[1].length);
      if (matchStart >= minIndex && (earliest < 0 || matchStart < earliest)) {
        earliest = matchStart;
      }
    }
  }
  return earliest;
};

/**
 * Static editor command at the start of a phrase; may leave remainder for chained commands.
 * @param {string} transcript
 * @param {Array<{ phrase: string, normalized: string, commandInfo: object }>} candidates
 * @param {string[]} navPhrases
 * @param {string[]} splitPhrases - phrases to detect a later command (header tail split)
 * @returns {{ commandInfo: object, remainder: string } | null}
 */
export const findLeadingStaticCommand = (
  transcript,
  candidates,
  navPhrases,
  splitPhrases = [],
  langCode = "en",
) => {
  const trimmed = stripBidi(transcript);
  if (!trimmed) return null;

  const normRem = normalizeCommandTranscript(trimmed, langCode);
  const navMatch = matchNavigationLeading(trimmed, navPhrases, langCode);
  if (navMatch) {
    let headerName = navMatch.remainder;
    let remainder = "";
    const splitAt = findEarliestCommandStartIndex(headerName, splitPhrases, {
      minIndex: 1,
    });
    if (splitAt > 0) {
      remainder = headerName.slice(splitAt).trim();
      headerName = headerName.slice(0, splitAt).trim();
    }
    return {
      commandInfo: {
        type: "navigation",
        action: "goTo",
        headerName,
      },
      remainder,
    };
  }

  const sorted = [...candidates].sort(
    (a, b) => b.normalized.length - a.normalized.length,
  );

  for (const c of sorted) {
    if (!c.normalized || !normRem.startsWith(c.normalized)) continue;

    if (normRem.length === c.normalized.length) {
      return { commandInfo: c.commandInfo, remainder: "" };
    }

    const after = sliceAfterLeadingPhrase(trimmed, c.phrase);
    if (after !== null && after !== trimmed) {
      return { commandInfo: c.commandInfo, remainder: after };
    }
    if (normRem.startsWith(c.normalized)) {
      const fallbackRemainder = remainderAfterPhraseWords(trimmed, c.phrase);
      return {
        commandInfo: c.commandInfo,
        remainder: fallbackRemainder,
      };
    }
  }

  const fuzzyLeading = findBestFuzzyLeadingStaticCommand(normRem, candidates);
  if (fuzzyLeading?.candidate) {
    const after = sliceAfterLeadingPhrase(trimmed, fuzzyLeading.candidate.phrase);
    return {
      commandInfo: fuzzyLeading.commandInfo,
      remainder:
        after !== null && after !== trimmed
          ? after
          : remainderAfterPhraseWords(
              trimmed,
              fuzzyLeading.candidate.phrase,
            ),
    };
  }

  const fuzzy = findBestFuzzyStaticCommand(normRem, candidates);
  if (fuzzy) {
    return { commandInfo: fuzzy.commandInfo, remainder: "" };
  }

  return null;
};
